import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { sidebarRegistry, type SidebarUseSharedState } from './SidebarRegistry';
import { workspaceStorage } from '../../Pages/Main/Ai/workspace/workspaceState.storage';

export type SidebarDisplayMode = 'overlay' | 'docked';

interface SidebarContextType {
  isOpen: boolean;
  activeId: string | null;
  props: any;
  displayMode: SidebarDisplayMode;
  /** Widened docked panel with a Rail; only meaningful when `displayMode === 'docked'`. */
  isExpanded: boolean;
  open: (id: string, props?: any) => void;
  close: () => void;
  setDisplayMode: (mode: SidebarDisplayMode) => void;
  toggleExpanded: () => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [props, setProps] = useState<any>(null);
  const [displayMode, setDisplayModeState] = useState<SidebarDisplayMode>(
    () => workspaceStorage.getSidebarDisplayMode(),
  );
  const [isExpanded, setIsExpanded] = useState(() => workspaceStorage.getSidebarExpanded());

  // Capture the element that had focus before opening so we can restore it on close.
  const previousFocusRef = useRef<Element | null>(null);

  const open = useCallback((id: string, newProps?: any) => {
    if (!sidebarRegistry.has(id)) {
      console.warn(`[SidebarProvider] Component with ID "${id}" is not registered in SidebarRegistry.`);
      return;
    }
    previousFocusRef.current = document.activeElement;
    setActiveId(id);
    setProps(newProps || null);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    // Keep activeId and props until transition finishes to avoid empty flashes
  }, []);

  // Restore focus to the element that triggered the open once the panel closes.
  // Runs after `isOpen` flips to false, after the exit-transition delay.
  useEffect(() => {
    if (!isOpen && previousFocusRef.current instanceof HTMLElement) {
      const el = previousFocusRef.current;
      previousFocusRef.current = null;
      // Defer to allow SidebarContainer's 300ms exit transition to finish.
      const id = setTimeout(() => {
        if (document.body.contains(el)) {
          el.focus();
        }
      }, 320);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [isOpen]);

  const setDisplayMode = useCallback((mode: SidebarDisplayMode) => {
    workspaceStorage.setSidebarDisplayMode(mode);
    setDisplayModeState(mode);
  }, []);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((current) => {
      const next = !current;
      workspaceStorage.setSidebarExpanded(next);
      return next;
    });
  }, []);

  return (
    <SidebarContext.Provider
      value={{
        isOpen,
        activeId,
        props,
        displayMode,
        isExpanded,
        open,
        close,
        setDisplayMode,
        toggleExpanded,
      }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

export const registerSidebar = (
  id: string,
  Content: React.ComponentType<any>,
  options?: {
    Rail?: React.ComponentType<any>;
    useSharedState?: SidebarUseSharedState;
    hideContainerControls?: boolean;
  }
) => {
  sidebarRegistry.register(id, { Content, ...options });
};
